import { BufferGeometry, Intersection, Points, Vector3 } from 'three'

interface RawPointData {
  point: Vector3
  index: number
  objectName?: string
}

export interface PointMetadata {
  rgb?: { r: number; g: number; b: number }
  thickness?: number
  rawData?: RawPointData
}

/**
 * Method to get the thickness and color of a point in a point cloud
 */
export function extractPointMetadata(
  intersection: Intersection<Points>
): PointMetadata | null {
  try {
    const point = intersection.point
    const object = intersection.object
    const index = intersection.index

    if (!point || index === undefined) {
      return null
    }

    const geometry = object.geometry
    if (!geometry) {
      return null
    }

    return {
      rawData: {
        point,
        index,
        objectName: object.name,
      },
      ...extractMetada(geometry, index),
    }
  } catch (error) {
    console.warn('Error extracting point color data:', error)
    return null
  }
}

/**
 * Extract Thickness and RGB color from point geometry
 */
function extractMetada(
  geometry: BufferGeometry,
  index: number
): Partial<PointMetadata> | undefined {
  const colorAttribute = geometry.attributes.rgba || geometry.attributes.color
  const thicknessAttribute = geometry.attributes.thickness

  if (
    colorAttribute &&
    colorAttribute.array &&
    thicknessAttribute &&
    thicknessAttribute.array
  ) {
    const colors = colorAttribute.array
    const stride = colorAttribute.itemSize
    const offset = index * stride

    const thicknesses = thicknessAttribute.array
    const thicknessIndex = index * thicknessAttribute.itemSize
    const thickness = thicknesses[thicknessIndex]

    if (offset + stride <= colors.length) {
      const r = colors[offset] || 0
      const g = colors[offset + 1] || 0
      const b = colors[offset + 2] || 0

      const normalizedR = r > 1 ? r / 255 : r
      const normalizedG = g > 1 ? g / 255 : g
      const normalizedB = b > 1 ? b / 255 : b

      return {
        thickness,
        rgb: {
          r: normalizedR,
          g: normalizedG,
          b: normalizedB,
        },
      }
    }
  }
}
